import React, { useState, useEffect } from 'react';
import { getMonthlyPayroll, submitPayroll } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Loader2, Calculator, Check, Clock, TrendingUp, TrendingDown, Minus, FileText } from 'lucide-react';

const PayrollPage = () => {
    const currentDate = new Date();
    const [year, setYear] = useState(currentDate.getFullYear());
    const [month, setMonth] = useState(currentDate.getMonth() + 1);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    
    // Submit dialog
    const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [billedHours, setBilledHours] = useState('');
    const [notes, setNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const months = [
        { value: 1, label: 'Januar' },
        { value: 2, label: 'Februar' },
        { value: 3, label: 'März' },
        { value: 4, label: 'April' },
        { value: 5, label: 'Mai' },
        { value: 6, label: 'Juni' },
        { value: 7, label: 'Juli' },
        { value: 8, label: 'August' },
        { value: 9, label: 'September' },
        { value: 10, label: 'Oktober' },
        { value: 11, label: 'November' },
        { value: 12, label: 'Dezember' }
    ];

    const years = [];
    for (let y = currentDate.getFullYear(); y >= currentDate.getFullYear() - 2; y--) {
        years.push(y);
    }

    useEffect(() => {
        loadData();
    }, [year, month]);

    const loadData = async () => {
        setLoading(true);
        try {
            const res = await getMonthlyPayroll(year, month);
            setData(res.data);
        } catch (err) {
            toast.error('Fehler beim Laden der Daten');
        } finally {
            setLoading(false);
        }
    };

    const openSubmitDialog = (employee) => {
        setSelectedEmployee(employee);
        setBilledHours(employee.billed_hours?.toString() || employee.recorded_hours?.toString() || '');
        setNotes(employee.notes || '');
        setSubmitDialogOpen(true);
    };

    const handleSubmit = async () => {
        if (!billedHours || isNaN(parseFloat(billedHours))) {
            toast.error('Bitte gültige Stundenzahl eingeben');
            return;
        }
        
        setSubmitting(true);
        try {
            await submitPayroll(selectedEmployee.employee_id, year, month, {
                billed_hours: parseFloat(billedHours),
                notes: notes || null
            });
            toast.success('Stunden an Lohnbüro gemeldet');
            setSubmitDialogOpen(false);
            loadData();
        } catch (err) {
            toast.error('Fehler beim Speichern');
        } finally {
            setSubmitting(false);
        }
    };

    const getBalanceIcon = (balance) => {
        if (balance > 0) return <TrendingUp className="h-4 w-4 text-green-600" />;
        if (balance < 0) return <TrendingDown className="h-4 w-4 text-red-600" />;
        return <Minus className="h-4 w-4 text-gray-400" />;
    };

    const getBalanceClass = (balance) => {
        if (balance > 0) return 'text-green-600 font-medium';
        if (balance < 0) return 'text-red-600 font-medium';
        return 'text-gray-500';
    };

    const formatBalance = (balance) => {
        if (balance > 0) return `+${balance.toFixed(1)}h`;
        return `${balance.toFixed(1)}h`;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6 space-y-6 pb-20" data-testid="payroll-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Calculator className="h-6 w-6" />
                        Stundenabrechnung
                    </h1>
                    <p className="text-muted-foreground">
                        Stunden an das Lohnbüro melden
                    </p>
                </div>
                <div className="flex gap-2">
                    <Select value={month.toString()} onValueChange={(v) => setMonth(parseInt(v))}>
                        <SelectTrigger className="w-[140px]">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {months.map(m => (
                                <SelectItem key={m.value} value={m.value.toString()}>
                                    {m.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
                        <SelectTrigger className="w-[100px]">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {years.map(y => (
                                <SelectItem key={y} value={y.toString()}>
                                    {y}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Info Card */}
            <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4">
                    <p className="text-sm text-blue-800">
                        <strong>So funktioniert es:</strong> Erfasste Stunden sind die Summe aller Zeiteinträge. 
                        Geben Sie die "abgerechneten Stunden" ein, die ans Lohnbüro gemeldet werden. 
                        Die Differenz wird als Saldo (Plus- oder Minusstunden) auf den nächsten Monat übertragen.
                    </p>
                </CardContent>
            </Card>

            {/* Employee List */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                        <span>{months.find(m => m.value === month)?.label} {year}</span>
                        <span className="text-sm font-normal text-muted-foreground">
                            {data?.employees?.length || 0} Mitarbeiter
                        </span>
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b">
                                    <th className="text-left py-3 px-2">Mitarbeiter</th>
                                    <th className="text-right py-3 px-2">Soll/Woche</th>
                                    <th className="text-right py-3 px-2">Erfasst</th>
                                    <th className="text-right py-3 px-2">Abgerechnet</th>
                                    <th className="text-right py-3 px-2">Übertrag</th>
                                    <th className="text-right py-3 px-2">Saldo</th>
                                    <th className="text-right py-3 px-2">Gesamt</th>
                                    <th className="text-center py-3 px-2">Aktion</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data?.employees?.map(emp => (
                                    <tr key={emp.employee_id} className="border-b hover:bg-muted/50">
                                        <td className="py-3 px-2">
                                            <div>
                                                <p className="font-medium">{emp.employee_name}</p>
                                                {emp.notes && (
                                                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                                                        <FileText className="h-3 w-3" />
                                                        {emp.notes}
                                                    </p>
                                                )}
                                            </div>
                                        </td>
                                        <td className="text-right py-3 px-2 text-muted-foreground">
                                            {emp.work_type === 'HOURLY' ? (
                                                <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-medium">SB</span>
                                            ) : (
                                                <span>{emp.weekly_hours}h</span>
                                            )}
                                        </td>
                                        <td className="text-right py-3 px-2">
                                            <span className="flex items-center justify-end gap-1">
                                                <Clock className="h-4 w-4 text-muted-foreground" />
                                                {emp.recorded_hours.toFixed(1)}h
                                            </span>
                                        </td>
                                        <td className="text-right py-3 px-2">
                                            {emp.billed_hours !== null ? (
                                                <span className="font-medium">{emp.billed_hours.toFixed(1)}h</span>
                                            ) : (
                                                <span className="text-muted-foreground">-</span>
                                            )}
                                        </td>
                                        <td className={`text-right py-3 px-2 ${getBalanceClass(emp.previous_balance)}`}>
                                            {emp.previous_balance !== 0 ? formatBalance(emp.previous_balance) : '-'}
                                        </td>
                                        <td className="text-right py-3 px-2">
                                            {emp.billed_hours !== null ? (
                                                <span className={`flex items-center justify-end gap-1 ${getBalanceClass(emp.balance)}`}>
                                                    {getBalanceIcon(emp.balance)}
                                                    {formatBalance(emp.balance)}
                                                </span>
                                            ) : (
                                                <span className="text-muted-foreground">-</span>
                                            )}
                                        </td>
                                        <td className={`text-right py-3 px-2 font-semibold ${getBalanceClass(emp.total_balance)}`}>
                                            {emp.billed_hours !== null ? (
                                                formatBalance(emp.total_balance)
                                            ) : (
                                                <span className="text-muted-foreground font-normal">
                                                    {emp.previous_balance !== 0 ? formatBalance(emp.previous_balance) : '-'}
                                                </span>
                                            )}
                                        </td>
                                        <td className="text-center py-3 px-2">
                                            <Button
                                                size="sm"
                                                variant={emp.billed_hours !== null ? "outline" : "default"}
                                                onClick={() => openSubmitDialog(emp)}
                                            >
                                                {emp.billed_hours !== null ? (
                                                    <>
                                                        <Check className="h-4 w-4 mr-1" />
                                                        Ändern
                                                    </>
                                                ) : (
                                                    'Abrechnen'
                                                )}
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>

            {/* Submit Dialog */}
            <Dialog open={submitDialogOpen} onOpenChange={setSubmitDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Stunden abrechnen</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="p-3 bg-muted rounded-lg">
                            <p className="font-medium">{selectedEmployee?.employee_name}</p>
                            <p className="text-sm text-muted-foreground">
                                {months.find(m => m.value === month)?.label} {year}
                            </p>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <p className="text-muted-foreground">Erfasste Stunden</p>
                                <p className="font-medium text-lg">{selectedEmployee?.recorded_hours.toFixed(1)}h</p>
                            </div>
                            <div>
                                <p className="text-muted-foreground">Übertrag Vormonat</p>
                                <p className={`font-medium text-lg ${getBalanceClass(selectedEmployee?.previous_balance || 0)}`}>
                                    {formatBalance(selectedEmployee?.previous_balance || 0)}
                                </p>
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Abgerechnete Stunden *</Label>
                            <Input
                                type="number"
                                step="0.5"
                                value={billedHours}
                                onChange={(e) => setBilledHours(e.target.value)}
                                onFocus={(e) => e.target.select()}
                                placeholder="z.B. 160"
                                data-testid="billed-hours-input"
                            />
                            {billedHours && selectedEmployee && (
                                <p className={`text-sm ${getBalanceClass(selectedEmployee.recorded_hours - parseFloat(billedHours))}`}>
                                    Saldo: {formatBalance(selectedEmployee.recorded_hours - parseFloat(billedHours))}
                                    {selectedEmployee.previous_balance !== 0 && (
                                        <span className="text-muted-foreground">
                                            {' '}(Gesamt: {formatBalance(selectedEmployee.previous_balance + selectedEmployee.recorded_hours - parseFloat(billedHours))})
                                        </span>
                                    )}
                                </p>
                            )}
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Notiz (optional)</Label>
                            <Textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="z.B. Korrektur wegen Krankheit"
                                rows={2}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSubmitDialogOpen(false)}>
                            Abbrechen
                        </Button>
                        <Button onClick={handleSubmit} disabled={submitting}>
                            {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                            An Lohnbüro melden
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default PayrollPage;
